"use client";
// src/hooks/auth-context.tsx
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import {onAuthStateChanged} from "firebase/auth";
import {auth, isFirebaseConfigured} from "@/config/firebase";
import type {HasuraUser} from "@/lib/hasura";

type AuthContextType = {
    userLoggedIn: boolean;
    currentUser: any;
    userData: HasuraUser | null;
    userLikes: number[];              // <— array di page IDs a cui hai messo like
    setCurrentUser: (u: any) => void;
};

export const AuthContext = createContext<AuthContextType>({} as any);

export const AuthProvider = ({children}: { children: ReactNode }) => {
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [userLoggedIn, setUserLoggedIn] = useState(false);
    const [userData, setUserData] = useState<HasuraUser | null>(null);
    const [userLikes, setUserLikes] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchUserData = async (fbUser: any) => {
        try {
            const token = await fbUser.getIdToken();
            const res = await fetch("/api/getUser", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({firebaseId: fbUser.uid}),
            });
            const {data} = await res.json();
            const u = data?.users?.[0] ?? null;
            setUserData(u);
            // estraiamo gli ID da `user_pages`
            setUserLikes(u?.user_pages?.map(up => Number(up.page)) ?? []);
        } catch (err) {
            console.error("Unable to fetch user data", err);
            setUserData(null);
            setUserLikes([]);
        }
    };

    async function initializeUser(user: any) {
        if (user) {
            setCurrentUser(user);
            await fetchUserData(user);
            setUserLoggedIn(true);
        } else {
            setCurrentUser(null);
            setUserLoggedIn(false);
            setUserData(null);
            setUserLikes([]);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (auth && isFirebaseConfigured()) {
            return onAuthStateChanged(auth, initializeUser);
        } else {
            // No Firebase auth available, set loading to false
            setLoading(false);
            return () => {};
        }
    }, []);

    const value = {
        userLoggedIn,
        currentUser,
        userData,
        userLikes,
        setCurrentUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    return useContext(AuthContext);
}
